// @owners { team: patients }
import { SPACING } from '@alto/design-library-tokens';
import {
  ActionSheetContext,
  ActionSheetV2,
  Body,
  Button,
  InlineAlert,
  LgPadding,
  LgSpacing,
  MdSpacing,
  Separator,
} from '@alto/design-system';
import { type AttestationRequestItem } from '@alto/scriptdash/alto/patient_app/attestations/v1/attestation_requests_endpoint';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import getPrescriptionPricingByID from '~shared/features/pricing/selectors/getPrescriptionPricingByID';
import { getUserSupportPhone } from '~shared/features/users/selectors/getUserSupportPhone';
import { getCurrentUserID } from '~shared/features/users/selectors/getUsers';
import { formatQuantity } from '~shared/helpers/helper';
import { useSelectorShared } from '~shared/store';
import { type Prescription } from '~shared/types';
import { TermsWebView } from '../../shared/components/TermsWebView';
import { USE_SOMETHING_ELSE_LABEL } from '../constants';
import { getDiscountedPriceDisplay, getRetailPriceDisplay } from '../helpers/getAttestationPriceDisplay';
import { getAttestationProgramContent } from '../helpers/getAttestationProgramContent';
import { AttestationActionSheet } from './AttestationActionSheet';
import { AttestationPriceRow } from './AttestationPriceRow';
import { ContactCareActionSheet } from './ContactCareActionSheet';

type Props = {
  attestationRequest: AttestationRequestItem;
  prescription: Prescription;
  onAddToCart: () => void;
};

const COPY = {
  secondary: 'Continue with retail price',
  tertiary: USE_SOMETHING_ELSE_LABEL,
  postAttestPrimary: 'Add to cart',
} as const;

export const AttestationPricingActionSheet = ({ attestationRequest, prescription, onAddToCart }: Props) => {
  const { closeActionSheet, setActiveActionSheet } = useContext(ActionSheetContext);
  const currentUserID = useSelectorShared(getCurrentUserID);
  const phoneNumber = useSelectorShared((state) => getUserSupportPhone(state, { userID: currentUserID }));
  const pricing = useSelectorShared((state) => getPrescriptionPricingByID(state, prescription.id));

  const [attested, setAttested] = useState(false);

  const programContent = getAttestationProgramContent(attestationRequest.program_config.billable_type);
  const quantity = formatQuantity(prescription);
  const priceDisplay = useMemo(
    () => (attested ? getDiscountedPriceDisplay(pricing) : getRetailPriceDisplay(pricing)),
    [attested, pricing],
  );

  const handleAddToCart = useCallback(() => {
    onAddToCart();
    closeActionSheet();
  }, [closeActionSheet, onAddToCart]);

  const handleOpenConsentSheet = useCallback(() => {
    setActiveActionSheet(
      <AttestationActionSheet
        attestationRequest={attestationRequest}
        onDismiss={() => {
          // Navigation is handled in AttestationActionSheet; dismiss analytics fire there.
        }}
        onSuccess={() => {
          setAttested(true);
        }}
      />,
      { shouldRetainPreviousActionSheet: true },
    );
  }, [attestationRequest, setActiveActionSheet]);

  const handleOpenContactSheet = useCallback(() => {
    setActiveActionSheet(<ContactCareActionSheet phoneNumber={phoneNumber} />, {
      shouldRetainPreviousActionSheet: true,
    });
  }, [phoneNumber, setActiveActionSheet]);

  const priceRow = (
    <AttestationPriceRow
      label={priceDisplay.label}
      price={priceDisplay.price}
      quantity={quantity || undefined}
    />
  );

  const buttons = attested
    ? [
        <Button
          key="add-to-cart"
          label={COPY.postAttestPrimary}
          onPress={handleAddToCart}
        />,
        <Button
          key="tertiary"
          label={COPY.tertiary}
          onPress={handleOpenContactSheet}
          type="tertiary"
        />,
      ]
    : [
        <Button
          key="secondary"
          label={COPY.secondary}
          onPress={handleAddToCart}
          type="secondary"
        />,
        <Button
          key="tertiary"
          label={COPY.tertiary}
          onPress={handleOpenContactSheet}
          type="tertiary"
        />,
      ];

  const terms = attestationRequest.statement.terms;

  return (
    <ActionSheetV2
      analyticsName="attestation_pricing_action_sheet"
      title={programContent.pricingTitle}
      buttons={buttons}
    >
      <LgPadding topPadding={SPACING.STATIC.NONE}>
        {terms ? (
          <>
            <TermsWebView htmlContent={terms} />
            <LgSpacing />
          </>
        ) : null}
        {attested ? (
          <>
            <InlineAlert type="success">
              <Body>{programContent.successToast}</Body>
            </InlineAlert>
            <LgSpacing />
            <Separator />
            <MdSpacing />
            {priceRow}
          </>
        ) : (
          <>
            <Button
              label={programContent.primaryCta}
              onPress={handleOpenConsentSheet}
            />
            <MdSpacing />
            <Separator />
            <MdSpacing />
            {priceRow}
          </>
        )}
      </LgPadding>
    </ActionSheetV2>
  );
};
